/**
 * ViewerToolbar - Compact toolbar for PACS viewer controls
 *
 * Organized as full-width rows within the 160px sidebar:
 * Row 1: Interaction tools (W/L, Pan, Zoom, Ruler) + Actions (Reset, Invert)
 * Row 2: Layout modes (2×2, 2×1, Single) + Reference Lines
 */

import React, { useCallback, useMemo } from "react";
import { useAtom, useAtomValue } from "jotai";
import { activeMeasurementToolAtom } from "../../atoms/measurementAtoms";
import {
  layoutModeAtom,
  LayoutMode,
  referenceLinesEnabledAtom,
  maximizedSlotAtom,
} from "../../atoms/hangingProtocolAtoms";
import { viewerSurfacesAtom } from "../../atoms/viewerSurfacesAtom";
import type { Surface } from "../../constants/viewerActions";
import {
  FaSun,
  FaArrowsAlt,
  FaArrowsAltH,
  FaSearchPlus,
  FaRuler,
  FaUndo,
  FaAdjust,
  FaTh,
  FaThLarge,
  FaColumns,
  FaSquare,
  FaCrosshairs,
  FaTrash,
  FaEraser,
} from "../../utils/icons";
import { projectGatingModeAtom } from "../../atoms/gatingAtoms";
import { GATING_MODE } from "../../types/gating";

export type ViewerTool = "windowLevel" | "pan" | "zoom" | "ruler" | "rectangleROI";

interface ViewerToolbarProps {
  onResetView?: () => void;
  onInvert?: () => void;
  onFlipHorizontal?: () => void;
  onDeleteAnnotation?: () => void;
  onClearAllAnnotations?: () => void;
  isInverted?: boolean;
  className?: string;
}

interface ToolButton {
  id: ViewerTool | "reset" | "invert" | "flipH" | "delete" | "clearAll";
  icon: React.ReactNode;
  label: string;
  shortcut?: string;
  /** Restrict button to a subset of surfaces. Omitted = universal. */
  surfaces?: Surface[];
}

// Rationale lives next to the Measurement Tools group in viewerActions.ts.
const MEASUREMENT_SURFACES: Surface[] = [
  "dictation",
  "labeling-conditions",
  "labeling-seg",
  "labeling-reporting",
];

const TOOLS: ToolButton[] = [
  {
    id: "windowLevel",
    icon: <FaSun className="w-3.5 h-3.5" />,
    label: "Window/Level",
    shortcut: "W",
  },
  {
    id: "pan",
    icon: <FaArrowsAlt className="w-3.5 h-3.5" />,
    label: "Pan",
    shortcut: "P",
  },
  {
    id: "zoom",
    icon: <FaSearchPlus className="w-3.5 h-3.5" />,
    label: "Zoom",
    shortcut: "Z",
  },
  {
    id: "ruler",
    icon: <FaRuler className="w-3.5 h-3.5" />,
    label: "Ruler",
    shortcut: "L",
    surfaces: MEASUREMENT_SURFACES,
  },
  {
    id: "rectangleROI",
    icon: <FaSquare className="w-3.5 h-3.5" />,
    label: "Rectangle ROI",
    shortcut: "M",
    surfaces: MEASUREMENT_SURFACES,
  },
  {
    id: "reset",
    icon: <FaUndo className="w-3.5 h-3.5" />,
    label: "Reset View",
    shortcut: "R",
  },
  {
    id: "invert",
    icon: <FaAdjust className="w-3.5 h-3.5" />,
    label: "Invert",
    shortcut: "I",
  },
  {
    id: "flipH",
    icon: <FaArrowsAltH className="w-3.5 h-3.5" />,
    label: "Flip horizontal (L/R)",
  },
  {
    id: "delete",
    icon: <FaEraser className="w-3.5 h-3.5" />,
    label: "Delete selected measurement",
    shortcut: "Del",
  },
  {
    id: "clearAll",
    icon: <FaTrash className="w-3.5 h-3.5" />,
    label: "Clear all measurements",
  },
];

type LayoutOption = { mode: LayoutMode; icon: React.ReactNode; label: string };

const LAYOUT_OPTIONS: LayoutOption[] = [
  { mode: "2x2", icon: <FaTh className="w-3 h-3" />, label: "2×2 Grid" },
  { mode: "2x1", icon: <FaColumns className="w-3 h-3" />, label: "2×1 Axial" },
  { mode: "single", icon: <FaSquare className="w-3 h-3" />, label: "Single" },
];

// View-dependent (stone) segmentation default: large axial + one switchable
// companion + 3D. Offered ONLY for view-dependent projects (prepended so it
// reads as the primary/default option).
const SEG_2UP_OPTION: LayoutOption = {
  mode: "seg-2up",
  icon: <FaThLarge className="w-3 h-3" />,
  label: "2-View + 3D",
};

export const ViewerToolbar: React.FC<ViewerToolbarProps> = ({
  onResetView,
  onInvert,
  onFlipHorizontal,
  onDeleteAnnotation,
  onClearAllAnnotations,
  isInverted = false,
  className = "",
}) => {
  const [activeMeasurementTool, setActiveMeasurementTool] = useAtom(activeMeasurementToolAtom);
  const [layoutMode, setLayoutMode] = useAtom(layoutModeAtom);
  const gatingMode = useAtomValue(projectGatingModeAtom);
  const layoutOptions = useMemo(
    () =>
      gatingMode === GATING_MODE.VIEW_DEPENDENT
        ? [SEG_2UP_OPTION, ...LAYOUT_OPTIONS]
        : LAYOUT_OPTIONS,
    [gatingMode],
  );
  const [referenceLinesEnabled, setReferenceLinesEnabled] = useAtom(referenceLinesEnabledAtom);
  const [, setMaximizedSlot] = useAtom(maximizedSlotAtom);
  const activeSurfaces = useAtomValue(viewerSurfacesAtom);
  const visibleTools = useMemo(
    () =>
      activeSurfaces === null
        ? TOOLS
        : TOOLS.filter((t) => !t.surfaces || t.surfaces.some((s) => activeSurfaces.includes(s))),
    [activeSurfaces],
  );

  const getActiveTool = (): ViewerTool => {
    switch (activeMeasurementTool) {
      case "pan":
        return "pan";
      case "zoom":
        return "zoom";
      case "length":
        return "ruler";
      case "rectangleROI":
        return "rectangleROI";
      default:
        return "windowLevel";
    }
  };

  const handleToolClick = useCallback(
    (toolId: ViewerTool | "reset" | "invert" | "flipH" | "delete" | "clearAll") => {
      switch (toolId) {
        case "windowLevel":
          setActiveMeasurementTool("none");
          break;
        case "pan":
          setActiveMeasurementTool("pan");
          break;
        case "zoom":
          setActiveMeasurementTool("zoom");
          break;
        case "ruler":
          setActiveMeasurementTool("length");
          break;
        case "rectangleROI":
          setActiveMeasurementTool("rectangleROI");
          break;
        case "reset":
          onResetView?.();
          break;
        case "invert":
          onInvert?.();
          break;
        case "flipH":
          onFlipHorizontal?.();
          break;
        case "delete":
          onDeleteAnnotation?.();
          break;
        case "clearAll":
          onClearAllAnnotations?.();
          break;
      }
    },
    [
      setActiveMeasurementTool,
      onResetView,
      onInvert,
      onFlipHorizontal,
      onDeleteAnnotation,
      onClearAllAnnotations,
    ],
  );

  const currentTool = getActiveTool();

  const btnClass = (active: boolean) =>
    `p-1 rounded transition-all duration-200 ${
      active
        ? "bg-primary/25 text-signal-text-tint ring-1 ring-primary/30"
        : "text-fg-muted hover:text-white hover:bg-white/10"
    }`;

  return (
    <div className={`space-y-1 ${className}`}>
      {/* Row 1: All tools. flex-wrap so the 8th button (Delete) doesn't get
          clipped by the sidebar's overflow:hidden when an extra tool is added. */}
      <div className="flex flex-wrap items-center justify-center gap-0.5 sidebar-card bg-bg-2 rounded-lg p-1">
        {visibleTools.map((tool) => {
          const isActive = tool.id === currentTool || (tool.id === "invert" && isInverted);
          return (
            <button
              key={tool.id}
              onClick={() => handleToolClick(tool.id)}
              className={btnClass(isActive)}
              title={tool.shortcut ? `${tool.label} (${tool.shortcut})` : tool.label}
            >
              {tool.icon}
            </button>
          );
        })}
      </div>

      {/* Row 2: Layout + Reference Lines */}
      <div className="flex items-center justify-center gap-0.5 sidebar-card bg-bg-2 rounded-lg p-1">
        {layoutOptions.map((option) => (
          <button
            key={option.mode}
            onClick={() => {
              setMaximizedSlot(null);
              setLayoutMode(option.mode);
            }}
            className={btnClass(layoutMode === option.mode)}
            title={option.label}
          >
            {option.icon}
          </button>
        ))}
        <div className="w-px h-4 bg-border mx-0.5" />
        <button
          onClick={() => setReferenceLinesEnabled(!referenceLinesEnabled)}
          className={btnClass(referenceLinesEnabled)}
          title="Reference Lines (X)"
        >
          <FaCrosshairs className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default ViewerToolbar;
